import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInterface = ({ userToken, onClose }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard?admin_token=${userToken}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load admin dashboard:', error);
      alert('Failed to load admin dashboard. You may not have admin privileges.');
    }
    setLoading(false);
  };

  const handleUserAction = async (userId, action, newQuota = null) => {
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/action?admin_token=${userToken}`, {
        action,
        new_quota: newQuota
      });
      alert(`User ${action} successful`);
      loadDashboard(); // Refresh data
    } catch (error) {
      alert(`Failed to ${action} user: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleApproveRequest = async (requestId, quota = 10) => {
    try {
      await axios.post(`${API_URL}/api/admin/approve-request/${requestId}?admin_token=${userToken}&quota=${quota}`);
      alert('Request approved successfully');
      loadDashboard();
    } catch (error) {
      alert(`Failed to approve request: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCreateCoupon = async (code, accessLevel, uses) => {
    try {
      await axios.post(`${API_URL}/api/admin/coupons?admin_token=${userToken}`, {
        code,
        access_level: accessLevel,
        uses_left: parseInt(uses)
      });
      alert('Coupon created successfully');
      loadDashboard();
    } catch (error) {
      alert(`Failed to create coupon: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!confirm(`Delete coupon ${code}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/coupons/${code}?admin_token=${userToken}`);
      alert('Coupon deleted successfully');
      loadDashboard();
    } catch (error) {
      alert(`Failed to delete coupon: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {['dashboard', 'users', 'requests', 'coupons'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium ${
                activeTab === tab
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800">Total Users</h3>
                  <p className="text-2xl font-bold text-blue-600">{dashboardData?.stats?.total_users || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800">AI Users</h3>
                  <p className="text-2xl font-bold text-green-600">{dashboardData?.stats?.ai_users || 0}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800">Basic Users</h3>
                  <p className="text-2xl font-bold text-gray-600">{dashboardData?.stats?.basic_users || 0}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-yellow-800">Pending Requests</h3>
                  <p className="text-2xl font-bold text-yellow-600">{dashboardData?.stats?.pending_requests || 0}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Recent Users</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">Username</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Email</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">AI Access</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Level</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Quota</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recent_users?.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">{user.username}</td>
                        <td className="border border-gray-300 px-4 py-2">{user.email}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.ai_access ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.ai_access ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="border border-gray-300 px-4 py-2">{user.access_level}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          {user.quota_remaining === -1 ? 'Unlimited' : user.quota_remaining}
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex space-x-2">
                            {!user.ai_access && (
                              <button
                                onClick={() => handleUserAction(user.id, 'enable')}
                                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                              >
                                Enable AI
                              </button>
                            )}
                            {user.ai_access && (
                              <button
                                onClick={() => handleUserAction(user.id, 'disable')}
                                className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                              >
                                Disable AI
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const quota = prompt('Enter new quota (or -1 for unlimited):');
                                if (quota !== null) handleUserAction(user.id, 'update_quota', parseInt(quota));
                              }}
                              className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                            >
                              Update Quota
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Pending Access Requests</h3>
              {dashboardData?.pending_requests?.length === 0 ? (
                <p className="text-gray-500">No pending requests</p>
              ) : (
                <div className="space-y-4">
                  {dashboardData?.pending_requests?.map((request) => (
                    <div key={request.id} className="border border-gray-300 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{request.username}</h4>
                          <p className="text-sm text-gray-600">{request.email}</p>
                          <p className="mt-2 text-sm">{request.reason}</p>
                          <p className="text-xs text-gray-400">
                            Requested: {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const quota = prompt('Grant quota (default 10):', '10');
                              if (quota !== null) handleApproveRequest(request.id, parseInt(quota) || 10);
                            }}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                          >
                            Approve
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'coupons' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-4">Create New Coupon</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleCreateCoupon(
                      formData.get('code'),
                      formData.get('access_level'),
                      formData.get('uses')
                    );
                    e.target.reset();
                  }}
                  className="flex space-x-4 items-end"
                >
                  <div>
                    <label className="block text-sm font-medium mb-1">Code</label>
                    <input
                      name="code"
                      required
                      className="px-3 py-2 border rounded-md"
                      placeholder="COUPON2024"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Access Level</label>
                    <select name="access_level" className="px-3 py-2 border rounded-md">
                      <option value="limited">Limited</option>
                      <option value="full">Full</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Uses</label>
                    <input
                      name="uses"
                      type="number"
                      required
                      className="px-3 py-2 border rounded-md"
                      placeholder="10"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Create
                  </button>
                </form>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">Existing Coupons</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(dashboardData?.invite_codes || {}).map(([code, details]) => (
                    <div key={code} className="border border-gray-300 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-lg">{code}</h4>
                          <p className="text-sm text-gray-600">
                            Level: <span className="capitalize">{details.access_level}</span>
                          </p>
                          <p className="text-sm text-gray-600">Uses left: {details.uses_left}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteCoupon(code)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInterface;